import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApiClient } from '../api/client';
import { LLMPricingEntry, TelemetryModelDetail } from '../types/admin';
import { calculateModelCost } from '../utils/format-cost';

interface ModelCostEntry {
  model: string;
  provider: string;
  cost: number;
}

interface TelemetryCostResult {
  totalCost: number;
  modelCosts: ModelCostEntry[];
  costByProvider: Record<string, number>;
  hasCostData: boolean;
  findPricing: (model: string) => LLMPricingEntry | undefined;
}

/**
 * Fetches LLM pricing and calculates per-model/provider costs
 * from telemetry model details.
 */
export function useTelemetryCost(
  modelDetails: TelemetryModelDetail[] | null,
): TelemetryCostResult {
  const { data: pricingData } = useQuery({
    queryKey: ['llm-pricing'],
    queryFn: () => adminApiClient.getPricing(),
    staleTime: 5 * 60 * 1000,
  });

  // Build lookup matching on both model and internal_model
  const pricingMap = useMemo(() => {
    const map = new Map<string, LLMPricingEntry>();
    if (pricingData?.data) {
      for (const p of pricingData.data) {
        map.set(p.model, p);
        map.set(p.internal_model, p);
      }
    }
    return map;
  }, [pricingData]);

  const findPricing = (model: string): LLMPricingEntry | undefined =>
    pricingMap.get(model);

  const { totalCost, modelCosts, costByProvider } = useMemo(() => {
    const costs: ModelCostEntry[] = [];
    let total = 0;

    if (modelDetails) {
      for (const m of modelDetails) {
        const pricing = pricingMap.get(m.model);
        if (pricing) {
          const cost = calculateModelCost(
            m.input_tokens,
            m.output_tokens,
            m.input_cached_tokens,
            pricing,
          );
          costs.push({ model: m.model, provider: m.provider, cost });
          total += cost;
        }
      }
    }

    const byProvider = costs.reduce<Record<string, number>>(
      (acc, { provider, cost }) => {
        acc[provider] = (acc[provider] || 0) + cost;
        return acc;
      },
      {},
    );

    return { totalCost: total, modelCosts: costs, costByProvider: byProvider };
  }, [modelDetails, pricingMap]);

  return {
    totalCost,
    modelCosts,
    costByProvider,
    hasCostData: totalCost > 0,
    findPricing,
  };
}
